import { useRef, useState } from 'react';
import { IFood, IOrders, ITopic } from '../pages/Dashboard/order.types';
import { formatHourMonthDayYear } from '../utils/handleDateTime';
import { useSettingsContext } from '../context/SettingsContext';
import { ISettings, IStadium } from '../pages/Settings/settings.types';
import { formatPriceItem, fetchMultipleSeatsByTableNumbers } from '../utils';
import { formatSeatInfo } from '../utils/seatHelpers';
import { useAuthContext } from '../context/AuthContext/AuthContext';
import { DELIVERY_FEE, ROLES } from '../consts/language';
import useLogPrinterStatus from './useLogPrinterStatus';
import { getDoc } from '../libs/firestore-wrapper';
import { getCurrentSettings } from '../modals/order';

interface IPrintOrder {
  data?: IOrders;
  ip_printer?: string;
  dataSettings?: ISettings;
  stadium?: IStadium;
  type?: string;
}

export const usePrinterWeb = () => {
  const ePosDev = useRef<any>(null);
  const { user, userInfo } = useAuthContext();
  const { mutateAsync } = useLogPrinterStatus();
  const { currentSetting, stadiumData } = useSettingsContext();
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const large_paper = currentSetting.large_paper ?? false;
  const restaurantNameCache = useRef<Record<string, string>>({});

  const loadEpsonSdk = (src = '/epos-2.27.0.js', timeout = 10000): Promise<void> => {
    return new Promise((resolve, reject) => {
      if ((window as any).epson && (window as any).epson.ePOSDevice) return resolve();

      const existing = Array.from(document.getElementsByTagName('script')).find((s) => s.getAttribute('src') === src);
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', (e) => reject(new Error('Failed to load Epson SDK')));
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = src;
      script.async = true;

      const timer = setTimeout(() => {
        script.onerror = null;
        script.onload = null;
        reject(new Error('Loading Epson SDK timed out'));
      }, timeout);

      script.onload = () => {
        clearTimeout(timer);
        if ((window as any).epson && (window as any).epson.ePOSDevice) resolve();
        else reject(new Error('Epson SDK loaded but global not found'));
      };

      script.onerror = () => {
        clearTimeout(timer);
        reject(new Error('Failed to load Epson SDK'));
      };

      document.head.appendChild(script);
    });
  };

  const processOrder = (printOrder: IPrintOrder) => {
    return new Promise<void>((resolve, reject) => {
      const { data, ip_printer, dataSettings, stadium, type } = printOrder;
      const cbConnectWrapper = (connectionData: any) => {
        cbConnect(data, connectionData, dataSettings, stadium, type, resolve, reject);
      };

      const connectPrinter = async () => {
        if (!isConnected) {
          try {
            await loadEpsonSdk();
            ePosDev.current = new (window as any).epson.ePOSDevice();
            await new Promise<void>((res, rej) => {
              ePosDev.current.connect(ip_printer ? ip_printer : currentSetting.ip_printer, 443, (connectionData: any) => {
                if (connectionData === 'OK' || connectionData === 'SSL_CONNECT_OK') {
                  setIsConnected(true);
                  res();
                } else {
                  console.debug('Connection failed:', connectionData);
                  rej(new Error('Failed to connect to printer'));
                }
              });
            });
            cbConnectWrapper('OK');
          } catch (error) {
            console.error('Error during printer connection:', error);
            ePosDev.current?.disconnect();
            if (data?.id && type) {
              await mutateAsync({
                order_id: data.id,
                print_message: `Connection error: ${error instanceof Error ? error.message : String(error)}`,
                type: type,
              });
            }
            setIsConnected(false);
            setTimeout(() => {
              addToQueue(printOrder);
              resolve();
            }, 5000);
          }
        } else {
          cbConnectWrapper('OK');
        }
      };

      connectPrinter();
    });
  };

  const cbConnect = async (
    data: IOrders | undefined,
    connectionData: any,
    dataSettings: ISettings | undefined,
    stadium: IStadium | undefined,
    type: string | undefined,
    resolve: () => void,
    reject: (reason?: any) => void
  ) => {
    if (connectionData === 'OK' || connectionData === 'SSL_CONNECT_OK') {
      try {
        ePosDev.current.createDevice('local_printer', ePosDev.current.DEVICE_TYPE_PRINTER, { crypto: false, buffer: false }, async (devobj: any, retcode: any) => {
          if (retcode === 'OK') {
            cbCreateDevice_printer(devobj, retcode, data, dataSettings, stadium, type, resolve, reject);
          } else {
            if (data?.id && type) {
              await mutateAsync({
                order_id: data.id,
                print_message: `Device creation failed: ${retcode}`,
                type: type,
              });
            }
            console.error('Device creation failed:', retcode);
            reject(new Error('Device creation failed'));
          }
        });
      } catch (error) {
        console.error('Error during device creation:', error);
        if (data?.id && type) {
          await mutateAsync({
            order_id: data.id,
            print_message: `Device creation error: ${error instanceof Error ? error.message : String(error)}`,
            type: type,
          });
        }
        reject(error);
      }
    } else {
      if (data?.id && type) {
        await mutateAsync({
          order_id: data.id,
          print_message: `Connection failed: ${connectionData}`,
          type: type,
        });
      }
      console.log('Connection failed:', connectionData);
      reject(new Error('Connection failed'));
    }
  };

  const cbCreateDevice_printer = async (
    devobj: any,
    retcode: any,
    data: IOrders | undefined,
    dataSettings: ISettings | undefined,
    stadium: IStadium | undefined,
    type: string | undefined,
    resolve: () => void,
    reject: (reason?: any) => void
  ) => {
    if (retcode === 'OK') {
      devobj.timeout = 5000;
      devobj.onreceive = async (res: any) => {
        console.debug('Print result:', res.success);
        if (data?.id && type) {
          await mutateAsync({
            order_id: data.id,
            print_message: `Print result: ${res.success}`,
            type: type,
          });
        }
      };
      devobj.oncoveropen = () => console.debug('Printer cover open');
      print(devobj, data, dataSettings, stadium, resolve);
    } else {
      console.debug(retcode);
      if (data?.id && type) {
        await mutateAsync({
          order_id: data.id,
          print_message: `Printing error: ${retcode}`,
          type: type,
        });
      }
      reject(new Error('Device creation failed'));
    }
  };

  const printHeaderSection = async (printer: any, data: IOrders, is_preorder?: boolean, stadium?: IStadium) => {
    printer.addTextAlign(printer.ALIGN_LEFT);
    printer.addFeedLine(1);
    printer.addTextPosition(10);
    const _preorderLabel = data?.is_pre_order ? ' PREORDER' : '';
    printer.addText(`Order ID: ${data.id}${_preorderLabel}\n`);
    printer.addTextPosition(20);
    if (data?.requested_delivery_time) {
      const displayDelivery =
        data?.delivery_request === 'WHEN_ITS_READY'
          ? `${formatHourMonthDayYear(data.requested_delivery_time).slashDate} 指定なし`
          : data?.delivery_request
            ? data.delivery_request
            : formatHourMonthDayYear(data.requested_delivery_time).fullDateTime;
      printer.addTextSize(2, 2);
      printer.addText(`配達予定時間:\n`);
      printer.addText(`${displayDelivery}\n\n`);
      printer.addTextSize(1, 1);
    } else {
      printer.addText(`Date: ${formatHourMonthDayYear(data.order_create_time).fullDateTime}\n\n`);
    }
    if (is_preorder) {
      printer.addTextPosition(20);
      printer.addText(`座席番号\n`);
      printer.addTextPosition(20);
      let seatLabel = '';
      const seatId = data?.order_table_number;
      const stadiumId = stadium?.id || stadiumData?.id;
      if (seatId && stadiumId) {
        try {
          const m = await fetchMultipleSeatsByTableNumbers(stadiumId, [seatId], true);
          const info = m.get(seatId);
          const formatted = formatSeatInfo(info);
          if (formatted) seatLabel = formatted;
        } catch (e) {
          // ignore
        }
      }
      const out = seatLabel;
      printer.addText(`${out}\n`);
    }
  };

  const printFoodItem = (printer: any, item: IFood) => {
    const maxCharsPerLineFontA = large_paper ? 43 : 29;
    const extraChars = 8;
    const quantity = item.item_deleted_quantity !== undefined ? item.item_quantity - item.item_deleted_quantity : item.item_quantity;
    printer.addTextPosition(30);
    printer.addText(`${quantity} x\t${wrapText(item.item_name, extraChars, maxCharsPerLineFontA - 5)}\n`);
    printer.addTextStyle(false, false, false, printer.COLOR_2);
    if (item.item_customize_settings && item.item_customize_settings.length > 0) {
      item.item_customize_settings.forEach((topping: ITopic) => {
        printer.addTextPosition(30);
        printer.addText(`  - ${topping.name}\n`);
      });
    }
    if (item.item_note !== '') {
      printer.addTextPosition(30);
      printer.addText(`  note: ${item.item_note}\n\n`);
    }
    printer.addTextStyle(false, false, false, printer.COLOR_1);
  };

  const calculateTotal = (data: IOrders) => {
    let totalPrice = 0;
    let totalCount = 0;
    let totalTax10 = DELIVERY_FEE;
    let totalTax8 = 0;
    let totalTaxAmount10 = 0;
    let totalTaxAmount8 = 0;

    data.foods.forEach((item: IFood) => {
      const quantity = item.item_deleted_quantity !== undefined ? item.item_quantity - item.item_deleted_quantity : item.item_quantity;
      const itemPrice = quantity * item.item_price;
      totalPrice += itemPrice;
      totalCount += quantity;

      const taxRate = parseFloat(item.tax_rate) ?? 8;
      const totalInclusive = itemPrice;
      let taxAmount = 0;
      if (totalInclusive > 0 && taxRate > 0) {
        const beforeTax = Math.round(totalInclusive / (1 + taxRate / 100));
        taxAmount = totalInclusive - beforeTax;
      }
      if (taxRate === 10) {
        totalTaxAmount10 += taxAmount;
        totalTax10 += totalInclusive;
      } else {
        totalTaxAmount8 += taxAmount;
        totalTax8 += totalInclusive;
      }
    });

    return {
      totalPrice,
      totalCount,
      totalTax10,
      totalTax8,
      totalTaxAmount10: totalTaxAmount10 > 0 ? totalTaxAmount10 + 77 : 70,
      totalTaxAmount8,
    };
  };

  const fetchRestaurantName = async (restaurantId: string): Promise<string> => {
    if (!restaurantId) return '';
    const cache = restaurantNameCache.current;
    if (cache[restaurantId]) return cache[restaurantId];
    try {
      const snap = await getDoc(getCurrentSettings(restaurantId));
      if (snap && snap.exists()) {
        const d: any = snap.data();
        const name = d?.name || '';
        cache[restaurantId] = name;
        return name;
      }
    } catch (e) {}
    return '';
  };

  const printOrderDetails = async (printer: any, data: IOrders, is_preorder?: boolean) => {
    if (!is_preorder) {
      data.foods.forEach((item: IFood) => {
        printFoodItem(printer, item);
      });
      return;
    }

    const getAllowedRestaurantIds = (): string[] => {
      let allowedRestaurantIds: string[] = [];
      if (userInfo?.role === ROLES.SUPER_ADMIN && Array.isArray(userInfo.restaurants)) {
        allowedRestaurantIds = userInfo.restaurants.filter((r: any) => r.is_admin_store).map((r: any) => r.id);
      } else if (userInfo?.restaurant_id) {
        allowedRestaurantIds = [userInfo.restaurant_id];
      }

      if (allowedRestaurantIds.length === 0 && data.foods.length > 0) {
        const firstRid = (data.foods[0] as any).restaurantId ?? null;
        if (firstRid) allowedRestaurantIds = [firstRid];
      }
      return allowedRestaurantIds;
    };

    const printGroupedFoods = async (foods: IFood[]) => {
      const groups: Record<string, IFood[]> = {};
      foods.forEach((f: IFood) => {
        const rid = (f as IFood).restaurantId ?? '';
        if (!groups[rid]) groups[rid] = [];
        groups[rid].push(f);
      });

      for (const rid of Object.keys(groups)) {
        const foods = groups[rid];
        const name = (await fetchRestaurantName(rid)) || rid;
        printer.addTextAlign(printer.ALIGN_CENTER);
        printer.addTextPosition(30);
        printer.addText(`\n${name}\n`);
        printer.addTextAlign(printer.ALIGN_LEFT);
        foods.forEach((item: IFood) => {
          printFoodItem(printer, item);
        });
      }
    };

    if (is_preorder) {
      const allowedRestaurantIds = getAllowedRestaurantIds();
      const filtered = data.foods.filter((f: IFood) => allowedRestaurantIds.includes((f as any).restaurantId));
      await printGroupedFoods(filtered);
      return;
    }
  };

  const formatRowWithSpacing = (label: string, count: number | string, sub_price: number, isUnitPrice = false, totalLength: number, price: number) => {
    const countText = `${count}`;
    const priceText = `¥${formatPriceItem(sub_price)}`;
    const fixedLeftSpace = large_paper ? totalLength - 28 : totalLength - 14;

    const usedLength = 3 + countText.length + priceText.length + fixedLeftSpace;
    const remainingSpaces = totalLength - usedLength;

    if (isUnitPrice) {
      const fixedLeftSpaceForEachPart = large_paper ? 8 : 4;
      const leftSpaceForLabel = fixedLeftSpace - (fixedLeftSpaceForEachPart + price.toString().length);

      return label + ' '.repeat(fixedLeftSpaceForEachPart) + `@${price}` + ' '.repeat(leftSpaceForLabel) + countText + ' '.repeat(remainingSpaces) + priceText;
    }

    return label + ' '.repeat(fixedLeftSpace) + countText + ' '.repeat(remainingSpaces) + priceText;
  };

  const formatRowWithAlignment = (label: string, value: string | number, totalLength: number) => {
    let labelLength = 0;
    for (let char of label) {
      labelLength += /[^\x00-\x7F]/.test(char) ? 2 : 1;
    }
    const valueText = `¥${formatPriceItem(value)}`;
    const valueLength = valueText.length;

    const remainingSpaces = totalLength - (labelLength + valueLength);

    return label + ' '.repeat(Math.max(0, remainingSpaces)) + valueText;
  };

  const printTotalSection = (
    data: IOrders,
    printer: any,
    totalCount: number,
    totalPrice: number,
    totalTax10: number,
    totalTaxAmount10: number,
    totalTax8: number,
    totalTaxAmount8: number,
    dataSettings?: ISettings
  ) => {
    const maxCharsPerLineFontA = large_paper ? 43 : 29;
    const maxCharsPerLineFontB = large_paper ? 52 : 35;
    const maxCharsPerLineFontALarge = large_paper ? 22 : 15;
    printer.addText('\n');
    printer.addTextFont(printer.FONT_A);
    printer.addTextPosition(30);
    printer.addText(formatRowWithSpacing('小計', totalCount, totalPrice, false, maxCharsPerLineFontA, 0) + '\n\n');
    printer.addTextPosition(30);
    printer.addText(formatRowWithAlignment('配達手数料', DELIVERY_FEE, maxCharsPerLineFontA + 1) + '\n\n');
    printer.addTextStyle(false, false, true, printer.COLOR_1);
    printer.addTextFont(printer.FONT_A);
    printer.addTextSize(2, 2);
    printer.addTextPosition(30);
    printer.addText(formatRowWithAlignment('合計', totalPrice + DELIVERY_FEE, maxCharsPerLineFontALarge) + '\n\n');
    printer.addTextSize(1, 1);
    printer.addTextPosition(30);
    printer.addTextFont(printer.FONT_B);
    printer.addTextStyle(false, false, false, printer.COLOR_1);
    printer.addText(formatRowWithAlignment('10%対象計', totalTax10, maxCharsPerLineFontB) + '\n');
    printer.addTextPosition(30);
    printer.addText(formatRowWithAlignment('(内消費税等', totalTaxAmount10, maxCharsPerLineFontB) + ')\n');
    printer.addTextPosition(30);
    printer.addText(formatRowWithAlignment('8%対象計', totalTax8, maxCharsPerLineFontB) + '\n');
    printer.addTextPosition(30);
    printer.addText(formatRowWithAlignment('(内消費税等', totalTaxAmount8, maxCharsPerLineFontB) + ')\n');
    printer.addTextPosition(30);
    printer.addText('※軽減税率 (8%）対象商品\n\n');
    printer.addTextPosition(30);
    printer.addText(`店舗: ${dataSettings ? (dataSettings.merchant_id ?? '') : (currentSetting.merchant_id ?? '')}\n`);
    printer.addTextPosition(30);
    printer.addText(`担当者: ${user?.email ?? ''}\n`);
    printer.addTextPosition(30);
    printer.addText(`取引番号: ${data.id}\n`);
  };

  const fetchImageAsBase64 = async (imageUrl: string): Promise<string> => {
    try {
      const response = await fetch(imageUrl, { mode: 'cors' });
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error('error:', error);
      return '';
    }
  };

  const addImageToPrint = async (printer: any, imageUrl: string): Promise<void> => {
    try {
      const img = await loadImage(imageUrl);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Error creating canvas');
      }

      const newWidth = large_paper ? 546 : 384;
      const newHeight = large_paper ? 450 : 290;

      canvas.width = newWidth;
      canvas.height = newHeight;
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      printer.addTextAlign(printer.ALIGN_CENTER);
      printer.addImage(ctx, 0, 0, newWidth, newHeight, printer.COLOR_1);
    } catch (error) {
      console.error('Failed to add image to print:', error);
    }
  };

  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = (error) => reject(error);
    });
  };

  const printStadiumInformation = async (printer: any, dataSettings?: ISettings, stadium?: IStadium) => {
    const imageUrl = stadium?.stadium_profile_pic || stadiumData?.stadium_profile_pic;
    if (imageUrl) {
      const base64Image = await fetchImageAsBase64(imageUrl);
      if (base64Image) {
        await addImageToPrint(printer, base64Image);
      }
    }
    printer.addText('\n');
    printer.addTextAlign(printer.ALIGN_CENTER);
    printer.addTextPosition(30);
    printer.addText('領収証\n\n');
    printBusinessDetails(printer, stadium);
    printer.addTextAlign(printer.ALIGN_LEFT);
    printer.addTextPosition(30);
    printer.addText(`${dataSettings ? (dataSettings.name ?? 'Restaurant') : (currentSetting.name ?? 'Restaurant')}\n`);
  };

  const printReceiptDetails = async (printer: any, data: IOrders, dataSettings?: ISettings, stadium?: IStadium) => {
    const maxCharsPerLineFontA = large_paper ? 43 : 29;
    await printStadiumInformation(printer, dataSettings, stadium);
    printer.addTextPosition(30);
    printer.addText(
      formatDateTimeWithAlignment(formatHourMonthDayYear(data.order_create_time).japaneseDate, formatHourMonthDayYear(data.order_create_time).time) + '\n\n'
    );
    const extraChars = 3;
    data.foods.forEach((item: IFood) => {
      printer.addTextPosition(30);
      printer.addText(wrapText(item.item_name, extraChars, maxCharsPerLineFontA) + '\n');
      printer.addTextPosition(30);
      const quantity = item.item_deleted_quantity !== undefined ? item.item_quantity - item.item_deleted_quantity : item.item_quantity;
      const price = quantity === 0 ? 0 : item.item_price;
      const total = parseFloat((quantity * price).toFixed(2));
      printer.addText(formatRowWithSpacing('※*', quantity, total, true, maxCharsPerLineFontA, price) + '\n');
    });
  };

  const printBusinessDetails = async (printer: any, stadium?: IStadium) => {
    if (stadium?.address || stadiumData.address) {
      printer.addTextAlign(printer.ALIGN_CENTER);
      printer.addTextPosition(30);
      printer.addText(`${stadium?.address || stadiumData.address}\n`);

      printer.addTextAlign(printer.ALIGN_CENTER);
      printer.addTextPosition(30);
      printer.addText(`TEL: ${stadium?.phone || stadiumData.phone}\n`);
    }

    if (stadium?.registration_code || stadiumData.registration_code) {
      printer.addTextAlign(printer.ALIGN_LEFT);
      printer.addTextPosition(30);
      printer.addText(`登録番号: ${stadium?.registration_code || stadiumData.registration_code}\n`);
    }

    if (stadium?.business_name || stadiumData.business_name) {
      printer.addTextAlign(printer.ALIGN_LEFT);
      printer.addTextPosition(30);
      printer.addText(`事業者名: ${stadium?.business_name || stadiumData.business_name}\n`);
    }
  };

  const formatDateTimeWithAlignment = (leftText: string, rightText: string) => {
    const maxCharsPerLineFontA = large_paper ? 43 : 29;
    const leftTextLength = 23;
    const rightTextLength = 5;
    const remainingSpaces = maxCharsPerLineFontA - (leftTextLength + rightTextLength);
    return leftText + ' '.repeat(remainingSpaces) + rightText;
  };

  function wrapText(text: string, indent: number, maxLineWidth: number): string {
    let lines: string[] = [];
    let currentLine = '';
    let currentWidth = 0;

    for (let char of text) {
      let charWidth = /[^\x00-\x7F]/.test(char) ? 2 : 1;
      if (currentWidth + charWidth > maxLineWidth) {
        lines.push(currentLine);
        currentLine = ' '.repeat(indent) + char;
        currentWidth = indent + charWidth;
      } else {
        currentLine += char;
        currentWidth += charWidth;
      }
    }

    if (currentLine) lines.push(currentLine);
    return lines.join('\n');
  }

  const print = async (printer: any, data: IOrders | undefined, dataSettings: ISettings | undefined, stadium: IStadium | undefined, resolve: () => void) => {
    try {
      printer.addTextLang('ja');
      if (!data || Object.keys(data).length === 0) {
        await printStadiumInformation(printer, currentSetting, stadiumData);
      } else {
        await printOrder(printer, data, dataSettings, stadium);
      }

      const shouldSkipPoweredBy = stadium?.cms_settings?.pre_order || stadiumData?.cms_settings?.pre_order;
      if (!shouldSkipPoweredBy) {
        printer.addTextAlign(printer.ALIGN_CENTER);
        printer.addText('Powered by tabilife.co.jp\n');
        printer.addFeedLine(1);
        printer.addCut(printer.CUT_FEED);
      } else {
        printer.addFeedLine(1);
        printer.addCut(printer.CUT_FEED);
      }
      printer.send();
      resolve();

      if (!data) {
        ePosDev.current?.disconnect();
        setIsConnected(false);
      }
    } catch (error) {
      console.error(error);
      resolve();
    }
  };

  const printOrder = async (printer: any, data: IOrders, dataSettings: ISettings | undefined, stadium: IStadium | undefined) => {
    const is_preorder = stadium?.cms_settings?.pre_order || stadiumData?.cms_settings?.pre_order;
    await printHeaderSection(printer, data, is_preorder, stadium);
    const { totalPrice, totalCount, totalTax10, totalTax8, totalTaxAmount10, totalTaxAmount8 } = calculateTotal(data);
    await printOrderDetails(printer, data, is_preorder);
    printer.addFeedLine(2);
    printer.addCut(printer.CUT_FEED);
    if (is_preorder) {
      await printHeaderSection(printer, data, is_preorder, stadium);
      await printOrderDetails(printer, data, is_preorder);
      return;
    }

    await printReceiptDetails(printer, data, dataSettings, stadium);
    printTotalSection(data, printer, totalCount, totalPrice, totalTax10, totalTaxAmount10, totalTax8, totalTaxAmount8, dataSettings);
  };

  const printQueue = useRef<IPrintOrder[]>([]);
  const isPrinting = useRef(false);
  const PROCESS_ORDER_DELAY_MS = 1000;

  const processQueue = async () => {
    if (isPrinting.current || printQueue.current.length === 0) return;
    isPrinting.current = true;
    const order = printQueue.current.shift();
    if (order) {
      try {
        await processOrder(order);
      } catch (error) {
        console.error('Error processing order:', error);
      }
    }
    await new Promise((resolve) => setTimeout(resolve, PROCESS_ORDER_DELAY_MS));
    isPrinting.current = false;
    if (printQueue.current.length > 0) {
      processQueue();
    }
  };

  const addToQueue = (order: IPrintOrder) => {
    printQueue.current.push(order);
    processQueue();
  };

  return { addToQueue };
};
